import type { TaskReference } from './types';

/**
 * Where a task reference opens -- the mechanism, not the destinations.
 *
 * A reference names the section that wrote it and carries that section's own
 * data. Only that section knows what is inside it or where it leads, so each
 * one registers its own resolver: nothing about absences, qualifications or
 * schedules is written here, and adding a section costs no change to this
 * file. The same arrangement as the backend, where a Django app claims its
 * reference type in `ready()`.
 */

export type TaskReferenceRoute = {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
};

export type TaskReferenceResolver = (
  data: Record<string, unknown>,
) => TaskReferenceRoute | null;

const resolvers: Record<string, TaskReferenceResolver> = {};

/** Declare where one section's references open. Called once, at startup. */
export function registerTaskReferenceResolvers(
  entries: Record<string, TaskReferenceResolver>,
): void {
  Object.assign(resolvers, entries);
}

export function canResolveTaskReferenceRoute(
  reference: TaskReference,
): boolean {
  return Boolean(reference.type && reference.type in resolvers);
}

export function resolveTaskReferenceRoute(
  reference: TaskReference,
): TaskReferenceRoute | null {
  if (!reference.type) {
    return null;
  }

  const resolve = resolvers[reference.type];
  if (!resolve) {
    return null;
  }

  return resolve(reference.data ?? {});
}
